import React, { Component } from "react";
import parsePhoneNumber from "libphonenumber-js";
import helpers from "../../helpers";
import firebase from "../../redux/store";
import {
  GoogleReCaptchaProvider,
  GoogleReCaptcha,
} from "react-google-recaptcha-v3";

import "intl-tel-input/build/css/intlTelInput.css";
const intlTelInput = require("intl-tel-input/build/js/intlTelInput.js");
import utils from "intl-tel-input/build/js/utils.js";

const md5 = require("md5");

import ReactCodeInput from "react-code-input";

// reCAPTCHA keys from https://www.google.com/recaptcha/admin
// V3 key must be from a key created as "reCAPTCHA type: v3" (e.g. maprix V3)
// V2 key must be from a key created as v2 Invisible or v2 Checkbox
const RECAPTCHA_V3_SITE_KEY = "6LdJBlQjAAAAAGU0DAW-rNh6cQONPiU-UimzzDxe";
const RECAPTCHA_V2_SITE_KEY = "6LcXA1QjAAAAAJr1fkSJmQMcmouxvegTl792tHuo";

export default class Login extends Component {
  constructor(props) {
    super(props);
    this.state = {
      recaptchaV3Token: null,
      useRecaptchaV2: false,
      recaptchaV2WidgetId: null,
      recaptchaV2Completed: false, // Track if V2 captcha has been completed
      mode: "phone",
      loading: false,
      wait: 0,
      phone: {
        text: "",
        number: "",
        dialCode: "",
        valid: false,
      },
      code: "",
      token: null,
      passwordExists: false,
      password: "",
      errorText: "",
      thumbnail: "",
      file: null,
      fileProgress: null,
    };
    this.iti;
    this.pendingV2Retry = null; // Store the 'via' parameter for V2 retry
    this.handleChange = this.handleChange.bind(this);
    this.handleVerify = this.handleVerify.bind(this);
    this.loadRecaptchaV2 = this.loadRecaptchaV2.bind(this);
    this.executeRecaptchaV2 = this.executeRecaptchaV2.bind(this);
  }

  componentDidMount() {
    this.initPhoneInput();
    // Preload reCaptcha V2 script (don't wait for it)
    this.loadRecaptchaV2().catch(() => {
      // Ignore errors during preload
    });
  }

  loadRecaptchaV2() {
    // Load reCaptcha V2 script if not already loaded
    return new Promise((resolve) => {
      if (window.grecaptcha && window.grecaptcha.render) {
        resolve();
        return;
      }

      // Check if script is already being loaded
      if (document.querySelector('script[src*="recaptcha/api.js"]')) {
        // Wait for it to load
        const checkInterval = setInterval(() => {
          if (window.grecaptcha && window.grecaptcha.render) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        resolve(); // Resolve anyway to prevent hanging
      };
      document.body.appendChild(script);
    });
  }

  executeRecaptchaV2() {
    return new Promise(async (resolve, reject) => {
      let timeoutId;
      let resolved = false;
      
      const cleanup = () => {
        resolved = true;
        if (timeoutId) clearTimeout(timeoutId);
      };
      
      try {
        console.log("Loading reCaptcha V2 script...");
        // Ensure script is loaded with timeout
        await Promise.race([
          this.loadRecaptchaV2(),
          new Promise((_, timeoutReject) => 
            setTimeout(() => timeoutReject(new Error("reCaptcha V2 script load timeout")), 10000)
          )
        ]);

        // Wait a bit more for grecaptcha to be fully ready
        let retries = 0;
        while ((!window.grecaptcha || !window.grecaptcha.render) && retries < 20) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }

        if (!window.grecaptcha || !window.grecaptcha.render) {
          console.error("reCaptcha V2 not available after loading");
          reject(new Error("reCaptcha V2 not available"));
          return;
        }

        console.log("reCaptcha V2 ready");

        // Check if widget already exists and reuse it
        let widgetId = this.state.recaptchaV2WidgetId;
        const container = document.getElementById("recaptcha-v2-container");
        
        // Set a timeout in case callback never fires (longer for visible captcha)
        timeoutId = setTimeout(() => {
          if (!resolved) {
            console.error("reCaptcha V2 execution timeout");
            reject(new Error("reCaptcha V2 execution timeout - please complete the verification"));
          }
        }, 120000); // 2 minutes for visible captcha

        if (widgetId === null || widgetId === undefined) {
          // Check if container already has a widget rendered (from previous attempt)
          if (container && container.hasChildNodes()) {
            console.log("Container has existing widget, clearing it first...");
            // Clear the container to allow re-rendering
            container.innerHTML = "";
          }

          console.log("Creating new visible reCaptcha V2 widget...");

          // Use VISIBLE reCaptcha V2 (normal checkbox)
          // Store callback reference so we can use it later
          const v2Callback = (token) => {
            console.log("reCaptcha V2 callback received token");
            cleanup();
            // Mark as completed in state
            this.setState({ recaptchaV2Completed: true });
            
            // Check if there's a pending resolver (from sendCode_SMS retry)
            if (this.v2TokenResolvers && this.v2TokenResolvers[widgetId]) {
              this.v2TokenResolvers[widgetId](token);
            } else {
              // Normal flow - resolve the promise
              resolve(token);
            }
          };
          
          widgetId = window.grecaptcha.render("recaptcha-v2-container", {
            sitekey: RECAPTCHA_V2_SITE_KEY,
            size: "normal", // Changed from "invisible" to "normal" for visible checkbox
            callback: v2Callback,
            "error-callback": () => {
              console.error("reCaptcha V2 error callback triggered");
              cleanup();
              this.setState({ recaptchaV2Completed: false });
              // Reject any pending resolver
              if (this.v2TokenResolvers && this.v2TokenResolvers[widgetId]) {
                this.v2TokenResolvers[widgetId](new Error("reCaptcha V2 verification failed"));
                delete this.v2TokenResolvers[widgetId];
              } else {
                reject(new Error("reCaptcha V2 verification failed"));
              }
            },
          });
          
          // Store callback for later use
          if (!this.v2Callbacks) this.v2Callbacks = {};
          this.v2Callbacks[widgetId] = v2Callback;

          console.log("reCaptcha V2 widget created, ID:", widgetId);
          this.setState({ recaptchaV2WidgetId: widgetId });
        } else {
          console.log("Reusing existing reCaptcha V2 widget, ID:", widgetId);
          
          // Reset the widget to get a fresh challenge
          try {
            window.grecaptcha.reset(widgetId);
            console.log("Widget reset successfully");
            // Reset completion state since we're getting a new challenge
            this.setState({ recaptchaV2Completed: false });
            
            // Update the callback to handle resolver pattern
            const v2Callback = (token) => {
              console.log("reCaptcha V2 callback received token");
              cleanup();
              this.setState({ recaptchaV2Completed: true });
              
              // Check if there's a pending resolver (from sendCode_SMS retry)
              if (this.v2TokenResolvers && this.v2TokenResolvers[widgetId]) {
                this.v2TokenResolvers[widgetId](token);
              } else {
                // Normal flow - resolve the promise
                resolve(token);
              }
            };
            
            // Update callback reference
            if (!this.v2Callbacks) this.v2Callbacks = {};
            this.v2Callbacks[widgetId] = v2Callback;
            
          } catch (e) {
            console.log("Error resetting widget:", e);
            // If reset fails, clear and recreate
            if (container) {
              container.innerHTML = "";
              this.setState({ recaptchaV2WidgetId: null });
              const v2Callback = (token) => {
                console.log("reCaptcha V2 callback received token");
                cleanup();
                this.setState({ recaptchaV2Completed: true });
                
                // Check if there's a pending resolver
                if (this.v2TokenResolvers && this.v2TokenResolvers[widgetId]) {
                  this.v2TokenResolvers[widgetId](token);
                } else {
                  resolve(token);
                }
              };
              widgetId = window.grecaptcha.render("recaptcha-v2-container", {
                sitekey: RECAPTCHA_V2_SITE_KEY,
                size: "normal",
                callback: v2Callback,
                "error-callback": () => {
                  console.error("reCaptcha V2 error callback triggered");
                  cleanup();
                  this.setState({ recaptchaV2Completed: false });
                  // Reject any pending resolver
                  if (this.v2TokenResolvers && this.v2TokenResolvers[widgetId]) {
                    this.v2TokenResolvers[widgetId](new Error("reCaptcha V2 verification failed"));
                    delete this.v2TokenResolvers[widgetId];
                  } else {
                    reject(new Error("reCaptcha V2 verification failed"));
                  }
                },
              });
              this.setState({ recaptchaV2WidgetId: widgetId });
              if (!this.v2Callbacks) this.v2Callbacks = {};
              this.v2Callbacks[widgetId] = v2Callback;
            }
          }
        }

        // For visible reCaptcha, we don't execute - user will click the checkbox
        // The callback will fire automatically when user completes it
        console.log("Visible reCaptcha V2 widget ready, waiting for user interaction...");
        
      } catch (error) {
        cleanup();
        // Check if it's the "already rendered" error
        if (error.message && error.message.includes("already been rendered")) {
          console.log("Widget already rendered error, clearing container and retrying...");
          const container = document.getElementById("recaptcha-v2-container");
          if (container) {
            container.innerHTML = "";
            // Reset widget ID so we create a new one
            this.setState({ recaptchaV2WidgetId: null });
            // Retry once
            try {
              const retryWidgetId = window.grecaptcha.render("recaptcha-v2-container", {
                sitekey: RECAPTCHA_V2_SITE_KEY,
                size: "normal", // Visible checkbox
                callback: (token) => {
                  console.log("reCaptcha V2 retry callback received token");
                  cleanup();
                  resolve(token);
                },
                "error-callback": () => {
                  console.error("reCaptcha V2 retry error callback triggered");
                  cleanup();
                  reject(new Error("reCaptcha V2 verification failed"));
                },
              });
              this.setState({ recaptchaV2WidgetId: retryWidgetId });
              return; // Don't reject, wait for user to complete captcha
            } catch (retryError) {
              console.error("Retry also failed:", retryError);
              reject(retryError);
            }
          } else {
            reject(error);
          }
        } else {
          console.error("Error in executeRecaptchaV2:", error);
          reject(error);
        }
      }
    });
  }

  initPhoneInput() {
    setTimeout(() => {
      if (this.state.mode == "phone") {
        this.$phoneInput = $("input[name=phone]");
        this.iti = intlTelInput(this.$phoneInput[0], {
          utilsScript: utils,
          preferredCountries: ["il"],
          nationalMode: true,
          formatOnDisplay: true,
          initialCountry: "auto",
          geoIpLookup: function (success, failure) {
            const countryCode = localStorage.getItem("countryCode");
            if (countryCode) success(countryCode);
            else {
              $.get(
                "https://ipinfo.io?token=cfab466ade60a0",
                function () {},
                "jsonp"
              ).always(function (resp) {
                console.log(resp);
                try {
                  var countryCode = resp && resp.country ? resp.country : "";
                  if (countryCode != "") {
                    localStorage.setItem("countryCode", countryCode);
                    success(countryCode);
                  } else success("IL");
                } catch (e) {
                  console.log(e);
                  success("IL");
                }
              });
            }
          },
        });
        this.$phoneInput.focus();
        this.$phoneInput.on("input", this.phoneInput.bind(this));
        this.$phoneInput.on("countrychange", this.phoneInput.bind(this));
      }
    }, 0);
  }

  phoneInput() {
    this.setState({
      phone: {
        number: this.iti.getNumber(),
        valid: this.iti.isValidNumber(),
        dialCode: this.iti.getSelectedCountryData().dialCode,
      },
    });
  }

  validatePhone(t, renew) {
    this.setState({ loading: true });
    // setTimeout to make sure reCaptcha is ready
    setTimeout(async () => {
      let hasPassword = false;
      if (!renew)
        hasPassword = await helpers.serviceRequest("clientHasPassword", {
          client: md5(this.state.phone.number),
        });
      try {
        this.iti.destroy();
      } catch (e) {}

      let askPassword = true;
      if (location.search.length > 0 && helpers.getUrlParameter("public")) {
        const file = helpers.getUrlParameter("file");
        const printer = helpers.getUrlParameter("printer");
        if (file && !printer) {
          this.setState({ file });
          askPassword = false;
          const fileRef = firebase
            .firestore()
            .collection("documents")
            .doc(file);
          fileRef.onSnapshot(async (snapshot) => {
            if (snapshot.exists) {
              const fileData = snapshot.data();
              const thumbnail = await helpers.serviceRequest("downloadPdf", {
                key: fileData.thumbnail,
              });

              const response = await helpers.serviceRequest("addFileToUser", {
                phone: this.state.phone.number,
                file,
              });
              console.log(response);

              this.setState({
                fileData,
                thumbnail: thumbnail.presigned_url,
              });
              setTimeout(() => {
                parent.window.close();
              }, 30000);
            } else {
              const _this = this;
              window.addEventListener(
                "message",
                function (event) {
                  const fileProgress = event.data;
                  if (fileProgress && fileProgress.fileId == _this.state.file) {
                    _this.setState({ fileProgress });
                    if (fileProgress.percent == 100) {
                      setTimeout(() => {
                        _this.setState({ fileProgress: null });
                      }, 1000);
                    }
                  }
                },
                false
              );
            }
          });
        }
      }

      if (askPassword) {
        this.setState({
          loading: false,
          mode: "password",
          password: "",
          passwordExists: hasPassword,
        });
      } else {
        this.setState({
          loading: false,
          mode: "file",
        });
      }
    }, 1000);
  }

  async validatePassword() {
    const password = md5(this.state.password);
    this.setState({
      loading: true,
      errorText: "The password is not correct. Please try again",
    });
    let data = { phone: this.state.phone.number, password };
    if (this.state.token) data.token = this.state.token;
    const response = await helpers.serviceRequest("validatePassword", data);
    if (response.valid && response.token) {
      firebase
        .auth()
        .signInWithCustomToken(response.token)
        .catch((error) => {
          console.log(error);
          this.setState({
            loading: false,
          });
        });
    } else {
      this.setState({ loading: false });
      this.verifyError();
    }
  }

  togglePasswordVisibility(a, b) {
    $("#togglePasswordVisibility")
      .find(".icon-toggle")
      .toggleClass("fa-eye fa-eye-slash");
    const $inputPassword = $("#togglePasswordVisibility").find("input");
    $inputPassword.attr("type") == "password"
      ? $inputPassword.attr("type", "text")
      : $inputPassword.attr("type", "password");
  }

  codeInput(value) {
    this.setState({
      code: value,
    });
  }

  handleChange(event) {
    const target = event.target;
    this.setState({ password: target.value });
  }

  sendCode_SMS() {
    // If V2 widget is shown, check if it's completed
    if (this.state.useRecaptchaV2) {
      if (!this.state.recaptchaV2Completed) {
        // User needs to complete the visible captcha first
        this.setState({
          errorText: "Please complete the verification checkbox above and try again.",
        });
        this.verifyError();
        return;
      } else {
        // V2 is completed - reset widget to get a fresh token (tokens are single-use)
        // But first, clear the error message and show a loading state
        console.log("V2 already completed, resetting widget to get new token...");
        if (this.state.recaptchaV2WidgetId !== null && window.grecaptcha) {
          try {
            // Reset widget to get a fresh challenge/token
            // This will clear the visual checkmark
            window.grecaptcha.reset(this.state.recaptchaV2WidgetId);
            this.setState({ 
              recaptchaV2Completed: false,
              errorText: "", // Clear any error messages
            });
            
            // Set up a promise to wait for the new token
            const getNewToken = new Promise((resolve, reject) => {
              const timeout = setTimeout(() => {
                reject(new Error("Timeout waiting for new V2 token"));
              }, 120000); // 2 minutes timeout
              
              // Store the resolve function so callback can use it
              if (!this.v2TokenResolvers) this.v2TokenResolvers = {};
              this.v2TokenResolvers[this.state.recaptchaV2WidgetId] = (token) => {
                clearTimeout(timeout);
                resolve(token);
              };
            });
            
            // Wait for user to complete captcha again, then retry
            getNewToken.then((v2Token) => {
              console.log("New V2 token obtained, retrying request...");
              delete this.v2TokenResolvers[this.state.recaptchaV2WidgetId];
              this.setState({ recaptchaV2Completed: true });
              this.sendCode("sms", v2Token);
            }).catch((error) => {
              console.error("Failed to get new V2 token:", error);
              delete this.v2TokenResolvers[this.state.recaptchaV2WidgetId];
              this.setState({
                errorText: "Please complete the verification checkbox to continue.",
                recaptchaV2Completed: false,
              });
              this.verifyError();
            });
            
            // Don't show error message - widget is reset and waiting for user action
            // The widget will show unchecked now, so user knows to complete it
            return;
          } catch (e) {
            console.error("Error resetting V2 widget:", e);
            // Fall through to normal flow
          }
        }
        // If widget doesn't exist or grecaptcha not available, fall through to normal flow
      }
    }
    
    // Normal flow - reset V2 fallback state when user manually triggers
    this.setState({ 
      useRecaptchaV2: false,
      recaptchaV2Completed: false 
    });
    this.sendCode("sms");
  }

  sendCode_CALL() {
    this.sendCode("call");
  }

  handleVerify(token) {
    if (!window.recaptchaV3Token) {
      this.setState({ recaptchaV3Token: token });
    }
    window.recaptchaV3Token = token;
  }

  async sendCode(via, recaptchaToken = null) {
    // Use provided token or fallback to V3, then V2
    let tokenToUse = recaptchaToken || window.recaptchaV3Token;
    
    const data = {
      via,
      phone_number: this.state.phone.number,
      country_code: this.state.phone.dialCode,
      code_length: 4,
      locale: "en",
    };

    // Add the appropriate reCaptcha token
    if (recaptchaToken) {
      // Try multiple possible field names for V2 token
      // Some backends might expect recaptchaToken, recaptchaV2Token, or recaptchaV3Token
      data.recaptchaToken = recaptchaToken;
      data.recaptchaV2Token = recaptchaToken;
      // Also try as V3 token field in case backend doesn't distinguish
      data.recaptchaV3Token = recaptchaToken;
    } else {
      data.recaptchaV3Token = tokenToUse;
    }

    this.setState({
      loading: true,
      errorText: "Your phone is not available. Please try again",
    });
    try {
      let verificationStart = await helpers.serviceRequest(
        "phoneVerificationStart",
        data
      );
      console.log(verificationStart);
      let message = verificationStart.message;
      // Extract phone number from message, with fallback to stored phone number
      let phoneText = this.state.phone.number; // Default to stored number
      if (message && message.indexOf("+") !== -1) {
        phoneText = message.substring(message.indexOf("+"), message.length - 1);
      }
      this.setState({
        mode: "code",
        code: "",
        loading: false,
        wait: 30,
        useRecaptchaV2: false, // Reset fallback flag on success
        phone: {
          number: this.state.phone.number,
          dialCode: this.state.phone.dialCode,
          text: phoneText,
        },
      });
      const waitInterval = setInterval(() => {
        const wait = this.state.wait - 1;
        this.setState({ wait });
        if (!wait) clearInterval(waitInterval);
      }, 1000);
    } catch (e) {
      console.log("sendCode error:", e);
      console.log("Error type:", e && e.error_type);
      console.log("Error object:", JSON.stringify(e));
      
      // Check if it's a reCaptcha error
      // Handle both direct error objects and nested error objects
      const errorObj = e && (e.error_type ? e : (e.responseJSON || e));
      
      console.log("Checking for RECAPTCHA_FAILED:", errorObj);
      
      if (errorObj && errorObj.error_type === "RECAPTCHA_FAILED") {
        console.log("RECAPTCHA_FAILED detected, showing V2 fallback");
        
        // If V3 failed and we haven't shown V2 yet, show visible V2 widget
        if (!recaptchaToken && !this.state.useRecaptchaV2) {
          console.log("Showing visible reCaptcha V2 widget...");
          this.setState({
            useRecaptchaV2: true,
            recaptchaV2Completed: false,
            errorText: "Please complete the verification below and try again.",
            loading: false,
          });
          // Show the error message immediately
          this.verifyError();
          
          // Initialize visible reCaptcha V2 widget (user will interact with it)
          // Store the via parameter so we can retry when captcha is completed
          this.pendingV2Retry = via;
          
          // Initialize the widget - callback will fire when user completes it
          this.executeRecaptchaV2().then((v2Token) => {
            console.log("reCaptcha V2 token obtained, retrying request...");
            // Mark V2 as completed
            this.setState({ recaptchaV2Completed: true });
            // Retry with V2 token when user completes captcha
            if (this.pendingV2Retry) {
              const retryVia = this.pendingV2Retry;
              this.pendingV2Retry = null;
              this.sendCode(retryVia, v2Token);
            }
          }).catch((v2Error) => {
            console.error("reCaptcha V2 initialization error:", v2Error);
            // Widget initialization failed - show error
            this.setState({
              errorText: "Failed to load verification. Please refresh the page.",
              loading: false,
              recaptchaV2Completed: false,
            });
            this.verifyError();
          });
          
          return; // Don't show error again, wait for user to complete V2
        } else if (recaptchaToken) {
          // V2 token was used but request still failed (could be Twilio error, etc.)
          console.log("V2 token was used but request failed:", errorObj.error);
          
          // Check if it's still a reCaptcha error or another error
          if (errorObj.error_type === "RECAPTCHA_FAILED") {
            // V2 also failed - reset and let user try again
            this.setState({
              errorText: "Verification failed. Please refresh the page and try again.",
              loading: false,
              useRecaptchaV2: false,
              recaptchaV2Completed: false,
            });
            // Reset widget visually
            if (this.state.recaptchaV2WidgetId !== null && window.grecaptcha) {
              try {
                window.grecaptcha.reset(this.state.recaptchaV2WidgetId);
              } catch (resetError) {
                console.log("Error resetting widget:", resetError);
              }
            }
          } else {
            // Other error (like Twilio) - show the actual error
            // Reset completion flag AND widget visually since token was used (tokens are single-use)
            // Keep widget visible so user can complete it again
            this.setState({
              errorText: errorObj.error || "Request failed. Please complete the verification again and try.",
              loading: false,
              recaptchaV2Completed: false, // Reset since token was used
            });
            // IMPORTANT: Reset the widget visually so user knows they need to complete it again
            if (this.state.recaptchaV2WidgetId !== null && window.grecaptcha) {
              try {
                window.grecaptcha.reset(this.state.recaptchaV2WidgetId);
                console.log("V2 widget reset after token was used and request failed");
              } catch (resetError) {
                console.log("Error resetting widget:", resetError);
              }
            }
          }
          this.verifyError();
        } else {
          // V2 widget is shown but user hasn't completed it yet
          if (!this.state.recaptchaV2Completed) {
            this.setState({
              errorText: "Please complete the verification below and try again.",
              loading: false,
            });
            this.verifyError();
          } else {
            // This shouldn't happen, but if V2 is completed but no token was passed
            // Reset completion and show error
            this.setState({
              errorText: errorObj.error || "Request failed. Please complete the verification again.",
              loading: false,
              recaptchaV2Completed: false,
            });
            this.verifyError();
          }
        }
      } else {
        // Other error (not reCaptcha) - extract error message from various possible structures
        let errorMessage = "Your phone is not available. Please try again";
        let errorCode = null;
        
        if (e) {
          if (typeof e === "string") {
            errorMessage = e;
          } else if (e.error) {
            errorMessage = e.error;
          } else if (e.message) {
            errorMessage = e.message;
          } else if (e.responseJSON && e.responseJSON.error) {
            errorMessage = e.responseJSON.error;
          }
          
          // Extract error code for specific handling
          errorCode = e.error_code || (e.responseJSON && e.responseJSON.error_code);
        }
        
        // Handle specific Twilio error codes with user-friendly messages
        if (errorCode === 60203) {
          // Max send attempts reached - rate limit
          errorMessage = "Too many verification attempts. Please wait 10-15 minutes before trying again, or use a different phone number for testing.";
        } else if (errorCode === 60410) {
          // Phone number blocked
          errorMessage = "This phone number has been temporarily blocked. Please try again later or use a different phone number.";
        }
        
        // If V2 is shown, handle error appropriately
        if (this.state.useRecaptchaV2) {
          // For all errors, reset widget visually since tokens are single-use
          // Even for rate limits, we need a new token when retrying
          if (errorCode === 60203 || errorCode === 60410) {
            // Rate limit - reset widget visually so user knows they need to complete it again
            // Tokens are single-use, so even for rate limits, we need a new token
            this.setState({
              errorText: errorMessage,
              loading: false,
              recaptchaV2Completed: false, // Will need new token when retrying
            });
            // Reset widget visually so user knows to complete it again
            if (this.state.recaptchaV2WidgetId !== null && window.grecaptcha) {
              try {
                window.grecaptcha.reset(this.state.recaptchaV2WidgetId);
                console.log("V2 widget reset after rate limit error - token was used");
              } catch (resetError) {
                console.log("Error resetting widget:", resetError);
              }
            }
          } else {
            // Other errors - reset completion flag since tokens are single-use
            this.setState({
              errorText: errorMessage,
              loading: false,
              recaptchaV2Completed: false, // Reset since we need a new token
            });
            // IMPORTANT: Reset the widget visually so user knows they need to complete it again
            // The token was used, so we need a fresh one
            if (this.state.recaptchaV2WidgetId !== null && window.grecaptcha) {
              try {
                window.grecaptcha.reset(this.state.recaptchaV2WidgetId);
                console.log("V2 widget reset after token was used and request failed");
              } catch (resetError) {
                console.log("Error resetting widget:", resetError);
              }
            }
          }
        } else {
          // Normal error handling - no V2 widget shown
          this.setState({
            errorText: errorMessage,
            loading: false,
            useRecaptchaV2: false,
            recaptchaV2Completed: false,
          });
        }
        this.verifyError();
      }
    }
  }

  verifyError() {
    this.setState({
      loading: false,
    });
    // Ensure the error element exists and show it
    const $errorElement = $("#verification-error");
    if ($errorElement.length) {
      // Update the text content to ensure it's current
      $errorElement.text(this.state.errorText || "An error occurred");
      $errorElement.addClass("snackbar-active");
      // Auto-hide after 5 seconds
      setTimeout(() => {
        $errorElement.removeClass("snackbar-active");
      }, 5000);
    } else {
      console.warn("Error element #verification-error not found");
      // Fallback: show alert if snackbar element doesn't exist
      if (this.state.errorText) {
        alert(this.state.errorText);
      }
    }
  }

  async verifyCode() {
    let data = {
      project: "prinity",
      token: true,
      phone_number: this.state.phone.number,
      country_code: this.state.phone.dialCode,
      code: this.state.code,
    };
    this.setState({
      loading: true,
      errorText: "The code is wrong. Please try again.",
    });
    try {
      let verificationCheck = await helpers.serviceRequest(
        "phoneVerificationCheck",
        data
      );
      console.log(verificationCheck);

      if (verificationCheck.success && verificationCheck.token) {
        this.setState({ token: verificationCheck.token });
        this.validatePhone(null, true);
      } else {
        this.verifyError();
      }
    } catch (e) {
      console.log(e);
      this.verifyError();
    }
  }

  onPhoneSubmit = (e) => {
    e.preventDefault();
    this.validatePhone();
  };
  onPasswordSubmit = (e) => {
    e.preventDefault();
    this.validatePassword();
  };
  onCodeSubmit = (e) => {
    e.preventDefault();
    this.verifyCode();
  };

  render() {
    let header, body, text;

    const loader = (
      <div className="lds-ring">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    );

    if (this.state.loading) text = loader;

    if (this.state.mode == "phone") {
      if (!this.state.loading) text = "Continue";
      header = (
        <div className="header header-fixed header-logo-left no-shadow">
          <a href="#" className="header-title ultrabold">
            Welcome
          </a>
        </div>
      );
      body = (
        <div>
          <div className="title bottom-20">What is your phone number?</div>
          <form onSubmit={this.onPhoneSubmit}>
            <div className="phoneInput">
              <input
                type="tel"
                name="phone"
                ref="phoneNumber"
                v-model="account.company.phone.phone_number"
                className="form-control"
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={!this.state.phone.valid}
              className={`${
                this.state.phone.valid ? "" : "disabled"
              } main-action-button button button-full button-3d button-m button-round-small border-dark1-dark bg-dark1-light ripple top-20 bottom-0`}
            >
              {text}
            </button>
          </form>
        </div>
      );
    } else if (this.state.mode == "password") {
      const title = this.state.passwordExists
        ? "Insert password"
        : "Create password";
      const subTitle = this.state.passwordExists
        ? "What is your password?"
        : "Please create a new password";

      header = (
        <div className="header header-fixed header-logo-app no-shadow">
          <a href="#" className="header-title ultrabold">
            {title}
          </a>
          <a
            href="#"
            onClick={() => {
              this.setState({
                mode: "phone",
                phone: { text: "", number: "", dialCode: "", valid: false },
              });
              this.initPhoneInput();
            }}
            className="header-icon header-icon-1"
            data-back-button=""
          >
            <i className="far fa-arrow-left"></i>
          </a>
        </div>
      );

      // forgot your password? send code / call me
      if (!this.state.loading) text = "Continue";

      let bottom;
      if (this.state.passwordExists) {
        bottom = (
          <div
            className="loginActions forgotPassword top-20"
            // style={{ direction: "rtl" }}
          >
            <div
              onClick={() => {
                if (this.state.loading) return;
                // If V3 token isn't ready yet (common on slow load or reCAPTCHA delays),
                // trigger V2 flow so user can still request a code
                if (!this.state.recaptchaV3Token && !this.state.useRecaptchaV2) {
                  this.setState({
                    useRecaptchaV2: true,
                    recaptchaV2Completed: false,
                    errorText: "Please complete the verification below and try again.",
                  });
                  this.verifyError();
                  this.pendingV2Retry = "sms";
                  this.executeRecaptchaV2()
                    .then((v2Token) => {
                      this.setState({ recaptchaV2Completed: true });
                      if (this.pendingV2Retry) {
                        const retryVia = this.pendingV2Retry;
                        this.pendingV2Retry = null;
                        this.sendCode(retryVia, v2Token);
                      }
                    })
                    .catch((err) => {
                      console.error("reCaptcha V2 error:", err);
                      this.setState({
                        errorText: "Verification failed to load. Please refresh and try again.",
                        useRecaptchaV2: false,
                      });
                      this.verifyError();
                    });
                  return;
                }
                this.sendCode_SMS();
              }}
              className={`${this.state.loading ? "disabled" : ""} button action ripple`}
            >
              Forgot your password?
            </div>
          </div>
        );
      }

      body = (
        <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_V3_SITE_KEY}>
          <div id="togglePasswordVisibility">
            <form onSubmit={this.onPasswordSubmit}>
              {/* Hidden username field for accessibility and password managers (phone is the identifier) */}
              <input
                type="text"
                name="username"
                autoComplete="username"
                value={this.state.phone.number || ""}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
                style={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  padding: 0,
                  margin: -1,
                  overflow: "hidden",
                  clip: "rect(0,0,0,0)",
                  whiteSpace: "nowrap",
                  border: 0,
                }}
              />
              <div className="title bottom-20">{subTitle}</div>
              {this.state.phone.number && (
                <div 
                  className="phone-display bottom-20" 
                  style={{ 
                    fontSize: "14px", 
                    color: "#666",
                    fontWeight: "normal"
                  }}
                >
                  {parsePhoneNumber(this.state.phone.number) 
                    ? parsePhoneNumber(this.state.phone.number).formatNational()
                    : this.state.phone.number}
                </div>
              )}
              <div className="input-style has-icon input-style-1 input-required">
                <i className="input-icon fad fa-lock font-11"></i>
                <i
                  onClick={this.togglePasswordVisibility.bind(this)}
                  className="icon-toggle input-icon fad fa-eye-slash"
                  style={{
                    left: "auto",
                    right: 0,
                    padding: 18 + "px",
                    paddingRight: 0,
                    marginTop: 0,
                  }}
                ></i>
                <input
                  type="password"
                  value={this.state.password}
                  onChange={this.handleChange}
                  name={
                    this.state.passwordExists
                      ? "current-password"
                      : "new-password"
                  }
                  autoComplete={
                    this.state.passwordExists
                      ? "current-password"
                      : "new-password"
                  }
                  placeholder="password"
                  maxLength="20"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={!(this.state.password.length >= 4)}
                className={`${
                  this.state.password.length >= 4 ? "" : "disabled"
                } main-action-button button button-full button-3d button-m button-round-small border-dark1-dark bg-dark1-light ripple top-20 bottom-0`}
              >
                {text}
              </button>
            </form>
            <GoogleReCaptcha
              onVerify={this.handleVerify}
              // refreshReCaptcha={this.state.refreshReCaptcha}
            />
            {/* Visible container for reCaptcha V2 fallback - shown when V3 fails */}
            {this.state.useRecaptchaV2 && (
              <div className="recaptcha-v2-wrapper" style={{ marginTop: "20px", marginBottom: "20px" }}>
                <div className="title bottom-10" style={{ fontSize: "14px", color: "#666" }}>
                  Please complete the verification below:
                </div>
                <div id="recaptcha-v2-container" style={{ display: "flex", justifyContent: "center" }}></div>
              </div>
            )}
            {bottom}
            {/* reCaptcha V2 invisible site key: 6LcXA1QjAAAAAJr1fkSJmQMcmouxvegTl792tHuo */}
            {/* reCaptcha V3 site key : 6LdJBlQjAAAAAGU0DAW-rNh6cQONPiU-UimzzDxe */}
          </div>
        </GoogleReCaptchaProvider>
      );
    } else if (this.state.mode == "code") {
      if (!this.state.loading) text = "Continue";
      header = (
        <div className="header header-fixed header-logo-app no-shadow">
          <a href="#" className="header-title ultrabold">
            Validate your phone number
          </a>
          <a
            href="#"
            onClick={() => {
              this.setState({
                mode: "phone",
                phone: { text: "", number: "", dialCode: "", valid: false },
              });
              this.initPhoneInput();
            }}
            className="header-icon header-icon-1"
            data-back-button=""
          >
            <i className="far fa-arrow-left"></i>
          </a>
        </div>
      );

      let bottom;
      if (this.state.wait) {
        bottom = (
          <span
            style={{
              position: "relative",
              top: 10 + "px" /* , direction: "rtl" */,
            }}
          >
            Please wait for the code {this.state.wait} seconds.
          </span>
        );
      } else {
        bottom = null;
        // bottom = (
        //   <div className="loginActions tryAgain top-20">
        //     <div
        //       onClick={this.sendCode_SMS.bind(this)}
        //       className={`${
        //         this.state.loading ? "disabled" : ""
        //       } button action ripple`}
        //     >
        //       Send SMS
        //     </div>
        //     <div
        //       onClick={this.sendCode_CALL.bind(this)}
        //       className={`${
        //         this.state.loading ? "disabled" : ""
        //       } button action ripple`}
        //     >
        //       Call me
        //     </div>
        //   </div>
        // );
      }

      body = (
        <div>
          <form onSubmit={this.onCodeSubmit}>
            <div className="title bottom-20">
              Please type the code sent to
              <br />
              {this.state.phone.text && parsePhoneNumber(this.state.phone.text) 
                ? parsePhoneNumber(this.state.phone.text).formatNational()
                : this.state.phone.number || "your phone"}
            </div>
            <ReactCodeInput
              type="tel"
              value={this.state.code}
              onChange={this.codeInput.bind(this)}
              fields={4}
              inputStyle={{ display: "flex" }}
            />
            <button
              type="submit"
              disabled={!(this.state.code.length == 4)}
              className={`${
                this.state.code.length == 4 ? "" : "disabled"
              } main-action-button button button-full button-3d button-m button-round-small border-dark1-dark bg-dark1-light ripple top-20 bottom-0`}
            >
              {text}
            </button>
          </form>
          {bottom}
        </div>
      );
    } else if (this.state.mode == "file") {
      if (this.state.fileData) {
        body = (
          <div>
            <h3 className="capitalize">Your file is ready</h3>
            <p
              className="under-heading capatalize bold bottom-20"
              style={{ color: "#0080a2", fontSize: 17 + "px" }}
            >
              Go to printer now!
            </p>
            <img
              src={this.state.thumbnail}
              style={{
                margin: "0 auto",
                width: 107 + "px",
                height: 150 + "px",
                border: "1px solid #d0d0d0",
              }}
            />
            <span style={{ color: "black" }}>
              Pages: {this.state.fileData.pages}
            </span>
          </div>
        );
      } else {
        if (this.state.fileProgress) {
          body = (
            <div>
              <h1 className="bottom-20">Preparing your file to print</h1>
              <div id="file-progress">
                <div
                  className="bar"
                  style={{ width: this.state.fileProgress.percent + "%" }}
                >
                  {this.state.fileProgress.percent}%
                </div>
              </div>
            </div>
          );
        } else {
          body = (
            <div>
              <h1 className="bottom-20">Preparing your file to print</h1>
              <div className="progress-indeterminate">
                <div className="line"></div>
                <div className="subline inc"></div>
              </div>
            </div>
          );
        }
      }
    }

    return (
      <div id="page" className="login">
        {header}
        <div className="page-content header-clear text-center">
          <div className="snackbars" style={{ top: 55 + "px", bottom: "auto" }}>
            <a href="#" id="verification-error">
              {this.state.errorText}
            </a>
          </div>
          <div className="caption-center">
            <div className="caption-content">
              <h1 className="center-text ultrabold fa-3x bottom-20">
                Maprint
                {/* <span
                  style={{
                    fontSize: "1rem",
                    marginLeft: "0.4rem",
                  }}
                >
                  (Prinity)
                </span> */}
              </h1>
              {body}
            </div>
          </div>
          <a
            href="https://maprint.com/terms.html"
            style={{
              position: "fixed",
              bottom: "10px",
              right: "10px",
              color: "black",
              fontSize: "14px",
            }}
          >
            Terms of use
          </a>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/pages/login.js